import { HelpCircle, LogOut, Menu, User } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { NotificationCenter, NotificationDropdown } from '@/components/features/notifications'
import { WorkspaceSelector } from '@/components/features/workspaces/WorkspaceSelector'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/contexts/AuthContext'
import { useNotificationPolling } from '@/hooks/useNotificationPolling'
import { useNotifications } from '@/hooks/useNotifications'

interface TopNavProps {
  onMenuClick: () => void
  onMobileMenuClick: () => void
}

export function TopNav({ onMenuClick, onMobileMenuClick }: TopNavProps) {
  const { user, logout } = useAuth()
  const [showNotificationCenter, setShowNotificationCenter] = useState(false)
  const navigate = useNavigate()

  // Use the notifications hook with real API integration
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    pagination,
    markAsRead,
    markMultipleAsRead,
    markAllAsRead,
    loadMore,
    refresh,
  } = useNotifications({
    pageSize: 20,
    pollingInterval: 0, // Disable internal polling to avoid duplicate API traffic
  })

  // Use lightweight polling for unread count with more frequent updates
  const { unreadCount: polledUnreadCount, checkNow } = useNotificationPolling({
    interval: 15000, // Poll every 15 seconds for unread count
    enabled: true,
    onNewNotifications: (count) => {
      // Show toast when new notifications arrive
      toast.info(`You have ${count} new notification${count > 1 ? 's' : ''}`)
      // Refresh the full notifications list
      refresh()
      // Also sync the polled count immediately
      checkNow()
    },
  })

  // Prefer the directly fetched count from useNotifications (e.g., after refresh/open),
  // and fall back to the polled count when unavailable.
  const currentUnreadCount = unreadCount ?? polledUnreadCount

  // Handle errors
  useEffect(() => {
    if (error) {
      console.error('Notification error:', error)
      // Optionally show a toast for user-facing errors
      if (error.message.includes('Network') || error.message.includes('fetch')) {
        toast.error('Unable to load notifications. Please check your connection.')
      }
    }
  }, [error])

  const handleMarkAsRead = async (id: string) => {
    try {
      await markAsRead(id)
    }
    catch (err) {
      toast.error('Failed to mark notification as read')
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead()
      toast.success('All notifications marked as read')
    }
    catch (err) {
      toast.error('Failed to mark all notifications as read')
    }
  }

  const handleBulkMarkAsRead = async (ids: string[]) => {
    try {
      await markMultipleAsRead(ids)
    }
    catch (err) {
      toast.error('Failed to mark notifications as read')
    }
  }

  const handleViewAll = () => {
    // Ensure the latest notifications are fetched before opening
    refresh()
    setShowNotificationCenter(true)
  }

  const handleLoadMore = async () => {
    try {
      await loadMore()
    }
    catch (err) {
      toast.error('Failed to load more notifications')
    }
  }

  return (
    <header className="w-full border-b bg-card shadow-sm">
      <div className="flex items-center h-16 px-4 lg:px-8">
        {/* Desktop Menu Toggle */}
        <Button variant="ghost" size="icon" className="hidden lg:flex" onClick={onMenuClick}>
          <Menu className="w-5 h-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>

        {/* Mobile Menu Toggle */}
        <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMobileMenuClick}>
          <Menu className="w-5 h-5" />
          <span className="sr-only">Toggle mobile menu</span>
        </Button>

        {/* Workspace Selector */}
        <div className="flex-1 px-4 lg:px-8">
          <WorkspaceSelector />
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <NotificationDropdown
            notifications={notifications}
            unreadCount={currentUnreadCount}
            onMarkAsRead={handleMarkAsRead}
            onMarkAllAsRead={handleMarkAllAsRead}
            onViewAll={handleViewAll}
            onOpen={async () => {
              await refresh()
              await checkNow()
            }}
            loading={isLoading}
            error={error}
          />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative rounded-full h-9 w-9">
                <Avatar className="h-9 w-9">
                  {/* <AvatarImage src={user?.profilePictureUrl || ''} alt={user?.firstName || ''} /> */}
                  <AvatarFallback>{user?.firstName?.charAt(0) || 'U'}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{user?.firstName}</p>
                  <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="w-4 h-4 mr-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <HelpCircle className="w-4 h-4 mr-4" />
                <span>Help & Support</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={logout}>
                <LogOut className="w-4 h-4 mr-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Notification Center Modal */}
      <NotificationCenter
        open={showNotificationCenter}
        onOpenChange={(open) => {
          setShowNotificationCenter(open)
          if (open)
            refresh()
        }}
        notifications={notifications}
        loading={isLoading}
        onMarkAsRead={handleBulkMarkAsRead}
        onMarkAllAsRead={handleMarkAllAsRead}
        onLoadMore={handleLoadMore}
        hasMore={pagination.hasMore}
        totalCount={pagination.totalCount}
      />
    </header>
  )
}
